import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSITIONS = [
  { id: "top-left", label: "Top-left", grid: "col-start-1 row-start-1" },
  { id: "top-right", label: "Top-right", grid: "col-start-2 row-start-1" },
  { id: "bottom-left", label: "Bottom-left", grid: "col-start-1 row-start-2" },
  { id: "bottom-right", label: "Bottom-right", grid: "col-start-2 row-start-2" },
];

/**
 * BrandPositionToggle — 2x2 grid of corner-preview tiles. Click a tile to set
 * where the DoozyGo® logo appears on every new image/video generated.
 * Applies to all future assets (published posts keep their original placement).
 */
export const BrandPositionToggle = ({ authAxios }) => {
  const [current, setCurrent] = useState("bottom-right");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/brand-position`, { timeout: 10000 });
      if (r.data?.position) setCurrent(r.data.position);
    } catch (e) { /* fall back to default */ }
    finally { setLoaded(true); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const choose = async (pos) => {
    if (pos === current || busy) return;
    setBusy(true);
    const prev = current;
    setCurrent(pos);  // optimistic
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/brand-position`, { position: pos });
    } catch (e) {
      setCurrent(prev);  // revert on error
      alert(e?.response?.data?.detail || "Could not update brand position");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="brand-position-toggle">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-1.5">
            DoozyGo® Logo Position
            {busy && <Loader2 size={12} className="animate-spin text-[#DC283C]" />}
          </h3>
          <p className="text-[11px] text-[#64748B] mt-0.5">
            Applies to every NEW image & video generated.
            Published assets keep their original placement.
          </p>
        </div>
        <div className="text-[10px] text-[#94a3b8] italic">
          Current: <span className="font-semibold text-[#1E1B4B]">{current}</span>
        </div>
      </div>

      {/* 2x2 preview grid — each tile is a miniature phone screen showing logo in that corner */}
      <div className="grid grid-cols-2 gap-2 w-64" style={{ gridTemplateRows: "1fr 1fr" }}>
        {POSITIONS.map((p) => {
          const isActive = p.id === current;
          const dotPosition = {
            "top-left": "top-1.5 left-1.5",
            "top-right": "top-1.5 right-1.5",
            "bottom-left": "bottom-1.5 left-1.5",
            "bottom-right": "bottom-1.5 right-1.5",
          }[p.id];
          return (
            <button
              key={p.id}
              onClick={() => choose(p.id)}
              disabled={busy || !loaded}
              data-testid={`brand-position-${p.id}`}
              title={p.label}
              className={`relative rounded border-2 transition-all h-16 ${
                isActive
                  ? "border-[#DC283C] bg-pink-50 shadow-sm"
                  : "border-gray-200 bg-gradient-to-br from-slate-50 to-slate-100 hover:border-[#DC283C]/50"
              } ${busy ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
            >
              {/* Pink dot = logo position preview */}
              <span
                className={`absolute w-4 h-4 rounded ${dotPosition} ${
                  isActive ? "bg-[#DC283C]" : "bg-gray-400"
                }`}
              />
              <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-[9px] text-[#64748B] text-center font-semibold">
                {p.label}
              </span>
              {isActive && (
                <Check size={12} className="absolute top-1 right-1 text-[#DC283C]" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
